// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { format } from 'date-fns';

import { Check, MapPin, Pen, Truck, Trash2, X } from 'lucide-react';
import Select from '../../components/ui/Select';
import { useTranslation } from '../../i18n/i18n';

export default function CorrectionRow({
  id,
  document,
  supplierName,
  fieldName,
  isEditing,
  editFieldContent,
  setEditFieldContent,
  suppliers,
  stockFields,
  linkedDevices,
  currentUser,
  onEdit,
  onAcceptEdit,
  onCancelEdit,
  onDelete,
}) {
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const supplierOptions = Object.entries(suppliers ?? {})
    .map(([key, value]) => {
      if (value?.supplier_code) {
        return { value: key, label: `${value?.supplier_code} ${value?.display_name}` };
      }
      return { value: key, label: value?.display_name ?? '' };
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

  const fieldOptions = Object.entries(stockFields ?? {})
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB, undefined, { numeric: true }))
    .map(([key, value]) => ({ value: key, label: value?.full_name }));

  return (
    <div className='w-full min-h-[6.125rem] rounded-2xl box-border p-4 flex gap-10 flex-row items-center bg-[#e7eef7] dark:bg-[#22293b] border border-[#e8efff] dark:border-white/[0.08]'>
      <div className='flex flex-col justify-center min-w-[9rem] text-[#16192c] dark:text-[#d2d2d2]'>
        <p className='text-base m-0 text-[#737580] font-medium dark:text-[#90a1b9] dark:font-normal'>
          {format(document.start_timestamp.toDate(), 'dd. MMM yyyy')}
        </p>
        <p className='text-base m-0 mt-4 text-[#737580] font-medium dark:text-[#90a1b9] dark:font-normal'>
          {format(document.start_timestamp.toDate(), 'HH:mm:ss')} -
          {'end_timestamp' in document ? format(document.end_timestamp.toDate(), 'HH:mm:ss') : t('common_now')}
        </p>
      </div>
      {isEditing ? (
        <>
          <div className='flex flex-1 gap-4'>
            <Select
              className='min-w-0 flex-1'
              prefix={<Truck size={18} />}
              options={supplierOptions}
              value={editFieldContent.supplier_id ?? ''}
              placeholder={t('button_grid_supplier_placeholder')}
              hasDeselectOption
              onChange={(next) =>
                setEditFieldContent((prev) => ({
                  ...prev,
                  supplier_id: next,
                }))
              }
            />
            <Select
              className='min-w-0 flex-1'
              prefix={<MapPin size={18} />}
              options={fieldOptions}
              value={editFieldContent.field ?? ''}
              placeholder={t('button_grid_field_placeholder')}
              hasDeselectOption
              onChange={(next) =>
                setEditFieldContent((prev) => ({
                  ...prev,
                  field: next,
                }))
              }
            />
          </div>
          <div className='flex'>
            <button
              className='flex justify-center items-center p-[10px] rounded-lg ml-auto mr-2 text-white bg-[#2563eb] hover:bg-[#235ad2] dark:bg-[#2773b7] dark:hover:bg-[#2368a5]'
              onClick={onAcceptEdit}
            >
              <Check />
            </button>
            <button
              className='flex justify-center items-center p-[10px] rounded-lg bg-[#f6f6f7] border border-[#d0d1d5] hover:bg-black/5 active:bg-black/10 dark:bg-[#0c121d] dark:border-[#3c3e53] dark:hover:bg-white/15 dark:active:bg-white/25'
              onClick={onCancelEdit}
            >
              <X />
            </button>
          </div>
        </>
      ) : (
        <>
          <div className='flex flex-col justify-center flex-1 text-[#16192c] dark:text-white gap-2'>
            {supplierName && (
              <p className='text-xl font-semibold m-0 text-[#2d3041] dark:text-[#fafafa]'>{supplierName}</p>
            )}
            <p className='text-lg m-0 text-[#5c5e6b] dark:text-[#cad5e2]'>
              {t('button_grid_field_prefix')} {fieldName}
            </p>
            {linkedDevices.length > 0 && (
              <p className='text-base m-0 mt-2'>{currentUser.devices?.[document?.device_id]?.display_name}</p>
            )}
          </div>
          <div className='flex'>
            <button
              className='flex justify-center items-center p-[10px] rounded-lg ml-auto mr-2 text-[#2773b7] border border-[#2563eb] bg-[#f6f6f7] hover:bg-black/5 active:bg-black/10 dark:border-[#2773b7] dark:bg-[#0c121d] dark:text-current dark:hover:bg-white/15 dark:active:bg-white/25'
              onClick={() => onEdit(id)}
            >
              <Pen />
            </button>
            <button
              className='flex justify-center items-center p-[10px] rounded-lg text-[#e04b4b] bg-[#f6f6f7] border border-[#e04b4b] hover:bg-black/5 active:bg-black/10 dark:bg-[#0c121d] dark:border-[#3c3e53] dark:hover:bg-white/15 dark:active:bg-white/25'
              onClick={() => onDelete(id)}
            >
              <Trash2 />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
